import { apiClient } from './client'
import { ApiError, type ProblemDetail } from './problem'
import { oidcUserManager } from '../auth/oidcUserManager'
import type {
  SpendImportBatchView,
  SpendImportCommitResultView,
  SpendImportPreviewView,
} from '../types/spendImport'

/**
 * Typed fetch wrappers for the spend-line import wizard (IB-184). Deliberately a NEW file, not an
 * addition to `api/film.ts` — `noRawSpendWrite.test.ts` locks that file down to exactly one
 * guarded-write shape (`adjustSpendLine`'s `{afterPct, comment}` POST); this wizard's own field is
 * `qualifyingPctOverride` (never the guarded `qualifyingPct` key literal) and its endpoints
 * (`/spend-imports/...`) never touch a `spend-lines/`/`qualified-spend-lines/` URL directly — the
 * backend's own `SpendLineImportService` is the only thing that ever calls
 * `QualifiedSpendLineService.create`/`SpendAdjustmentService.adjustSpendLine`.
 *
 * `uploadSpendImportBatch` cannot use the shared `apiClient` (JSON-only) — it POSTs
 * `multipart/form-data`, so it builds its own request, mirroring `apiClient`'s own bearer-token
 * and RFC 9457 problem-body handling rather than duplicating it silently differently.
 */

const API_BASE = '/api/v1'

async function authHeader(): Promise<HeadersInit> {
  const user = await oidcUserManager.getUser()
  return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {}
}

async function toApiError(res: Response): Promise<ApiError> {
  let problem: ProblemDetail
  try {
    const body = (await res.json()) as Partial<ProblemDetail>
    problem = { status: res.status, ...body }
  } catch {
    problem = { status: res.status, title: res.statusText }
  }
  return new ApiError(problem)
}

/** Step 1 — upload (multipart). Returns detected headers + a best-effort suggested column mapping. */
export async function uploadSpendImportBatch(
  productionId: string,
  fiscalYearId: string,
  file: File,
): Promise<SpendImportBatchView> {
  const form = new FormData()
  form.append('fiscalYearId', fiscalYearId)
  form.append('file', file)

  const res = await fetch(`${API_BASE}/productions/${productionId}/spend-imports`, {
    method: 'POST',
    headers: await authHeader(),
    body: form,
  })
  if (!res.ok) {
    throw await toApiError(res)
  }
  return (await res.json()) as SpendImportBatchView
}

/** Step 2 — dry-run preview (ZERO writes server-side). Re-submits the column mapping every call. */
export async function previewSpendImportBatch(
  batchId: string,
  columnMapping: Record<string, string>,
): Promise<SpendImportPreviewView> {
  const { data } = await apiClient.post<SpendImportPreviewView>(`/spend-imports/${batchId}/preview`, {
    columnMapping,
  })
  return data
}

/** Step 3 — transactional, idempotent commit. */
export async function commitSpendImportBatch(
  batchId: string,
  columnMapping: Record<string, string>,
): Promise<SpendImportCommitResultView> {
  const { data } = await apiClient.post<SpendImportCommitResultView>(`/spend-imports/${batchId}/commit`, {
    columnMapping,
  })
  return data
}
